import { DataActions } from "../store/data/actions";
import { IFeatureFlagStatus } from "../store/data/types";
import { DriveStore } from "../store/reducers";
import { CancelablePromise } from "../utils/cancelablePromise";
import { handleApi } from "../utils/error";
import { FeatureFlagName } from "../utils/featureFlagName";
import Api from "./api";
import { Unsubscribe } from "../lib/redux";
import { observeStore } from "../utils/store";
import { emptyFeatureFlagLocalStorage } from "../isFeatureFlagsEnabled";
import * as logger from "../utils/logger";

export { isFeatureFlagOverrideAllowed } from "../utils/featureFlagOverrides";

/**
 * Feature flags that are always enabled in the app, regardless of server settings.
 * Add any feature flags here that should be auto-enabled (e.g., for testing purposes in the pipeline).
 */
const AUTO_ENABLED_FEATURE_FLAGS: FeatureFlagName[] = [];

type FeatureFlagNameKey = keyof typeof FeatureFlagName;

export type TFeatureFlagValue = string | number | Date;

/**
 * @deprecated Not supported. Use the IFeatureFlagsService instead.
 * Temporary method for utils or helpers that have difficulty gaining access to FeatureFlag service
 * @param flag The FeatureFlag to check
 * @returns boolean
 */
export let hasFeatureFlagLegacy = (flag: FeatureFlagName) => {
    return window.driveApp?.store?.getState()?.data?.featureFlags[flag]?.isEnabled ?? false;
};

/**
 * @deprecated Not supported. Use the IFeatureFlagsService instead.
 * Temporary method for utils or helpers that have difficulty gaining access to FeatureFlag service.
 * Returns the string value of a feature flag if it is enabled and has a value, otherwise returns the defaultValue.
 * @param flag The FeatureFlag to read the value from
 * @param defaultValue The fallback value if the flag is disabled or has no value
 * @returns string
 */
export let getFeatureFlagValueLegacy = (flag: FeatureFlagName, defaultValue: string): string => {
    const flagStatus = window.driveApp?.store?.getState()?.data?.featureFlags[flag];
    if (!flagStatus?.isEnabled || !flagStatus?.value) {
        return defaultValue;
    }
    return flagStatus.value as string;
};

export interface IFeatureFlagsService {
    /**
     * Download Drive-specific feature flags from MyGeotab server.
     * Called on regular basis to keep Drive in sync with db.
     * @returns void
     */
    downloadFeatureFlags: (api: Api) => CancelablePromise<void>;

    /**
     * Downloads feature flags that do not require authentication
     * @returns void
     */
    downloadNoAuthFeatureFlags: (flags: FeatureFlagName[]) => CancelablePromise<void>;

    /**
     * @returns Whole feature flag object from the Store
     */
    getAllFeatureFlags: () => IFeatureFlagStatus[];

    /**
     * @returns A comma seperated string of all enabled feature flags
     */
    getAllEnabledFeatureFlagCsv: () => string;

    /**
     * Check whether provided feature flag is enabled.
     * @param flag The FeatureFlagName to check
     * @returns true/false
     */
    hasFeatureFlag: (flag: FeatureFlagName) => boolean;

    /**
     * Get the value of a provided feature flag
     * @param flag The FeatureFlagName to get value from
     * @param defaultValue The default value return if no value is present in the feature flag
     * @returns
     */
    getFeatureFlagValue: (flag: FeatureFlagName, defaultValue: TFeatureFlagValue) => TFeatureFlagValue;

    /**
     * Observes changes to the provided feature flag (ie. disabled/enabled or value).
     * @param flag The FeatureFlagName to check
     * @param onChange The callback function called upon a feature flag change
     * @returns A function to remove this change listener
     */
    observeFlagChanges: (flag: FeatureFlagName, onChange: (flagStatus: IFeatureFlagStatus) => void) => Unsubscribe;
}

/**
 * A service that controls FeatureFlags, synchronizes remote/local IFeatureFlagStatus data
 */
export class FeatureFlags implements IFeatureFlagsService {
    constructor(
        private readonly store: DriveStore
    ) {
    }

    static setFeatureFlags(store: DriveStore, featureFlagsDict: { [key: string]: IFeatureFlagStatus }): void {
        store.dispatch(DataActions.setFeatureFlags(featureFlagsDict));
    }

    static convertToFeatureFlagsDict(featureFlags: IFeatureFlagStatus[]): IIdDictionary<IFeatureFlagStatus> {
        return featureFlags.reduce((prev, curr: IFeatureFlagStatus) => {
            prev[curr.name] = curr;
            return prev;
        }, {} as IIdDictionary<IFeatureFlagStatus>);
    }

    /**
     * Downloads unauthenticated feature flags requested based on a `FeatureFlagName` array passed into the function
     *
     * Must ensure the feature flags in question are in the CustomCodes.cs `QueryableFlagsWithoutAuth` HashSet, otherwise
     * the default value is `false`
     * @param flags An array of unauthenticated feature flags that we want to query
     */
    downloadNoAuthFeatureFlags(flags: FeatureFlagName[]): CancelablePromise<void> {
        return new CancelablePromise(window.isFeatureFlagsEnabled(flags, this.store))
            .then(results => {
                let noAuthFeatureFlags = FeatureFlags.convertToFeatureFlagsDict(results);
                // Guarantee every requested flag has an entry so featureFlags[FLAG] is never undefined.
                for (const flag of flags) {
                    if (!noAuthFeatureFlags[flag]) {
                        noAuthFeatureFlags[flag] = { name: flag, isEnabled: false };
                    }
                }
                let flagsToSetIntoStorage = { ... this.store.getState().data.featureFlags, ...noAuthFeatureFlags};
                FeatureFlags.setFeatureFlags(this.store, flagsToSetIntoStorage);
                emptyFeatureFlagLocalStorage();
            });
    }

    /**
     * Downloads Drive-specific feature flags via the authenticated endpoint.
     * Only flags defined in the FeatureFlagName enum are requested, avoiding
     * the deprecated GetAllFeatureFlags bulk download.
     */
    downloadFeatureFlags(api: Api): CancelablePromise<void> {
        let driveFlags = Object.keys(FeatureFlagName).map(key => FeatureFlagName[key as FeatureFlagNameKey]);
        return api.callPromise("GetFeatureFlagAuthenticated", { flags: driveFlags })
            .then((response: IFeatureFlagStatus[] | null | undefined) => {
                if (!response || !Array.isArray(response)) {
                    return;
                }
                const dict = FeatureFlags.convertToFeatureFlagsDict(response);
                // Guarantee every known flag has an entry so featureFlags[FLAG] is never undefined.
                // When the server drops a flag, code that accesses .isEnabled without ?. would crash otherwise.
                for (const flag of driveFlags) {
                    if (!dict[flag]) {
                        dict[flag] = { name: flag, isEnabled: false };
                    }
                }
                FeatureFlags.setFeatureFlags(this.store, dict);
            })
            .catch(error => {
                handleApi(error);
            });
    }

    getAllFeatureFlags() {
        let flags = this.store.getState().data.featureFlags;
        return Object.keys(flags).map(function(key) {
            return flags[key];
        });
    }

    getAllEnabledFeatureFlagCsv() {
        return this.getAllFeatureFlags()
            .filter(flag => flag.isEnabled)
            .map(flag => flag.name + (flag.value ? `=${ flag.value }` : ""))
            .join(",");
    }

    hasFeatureFlag(flag: FeatureFlagName): boolean {
        if (AUTO_ENABLED_FEATURE_FLAGS.indexOf(flag) !== -1) {
            return true;
        }
        return this.store.getState().data.featureFlags[flag]?.isEnabled ?? false;
    }

    getFeatureFlagValue<T extends TFeatureFlagValue>(flag: FeatureFlagName, defaultValue: T): T {
        // check if ff is even enable
        let hasFlag = this.hasFeatureFlag(flag);
        if (!hasFlag) {
            return defaultValue;
        }

        // check if value exists
        let value = this.store.getState().data.featureFlags[flag]?.value;
        if (!value) {
            return defaultValue;
        }

        // check what type of value to return
        if (typeof defaultValue === "number") {
            const num = parseInt(value, 10);
            if (isNaN(num)) {
                let errorMessage = `Invalid input: FF value cannot be parsed as an integer. value: ${ value }`;
                logger.error(logger.Source.ERROR, errorMessage);
                throw new TypeError(errorMessage);
            }
            return num as T;
        }

        if (typeof defaultValue === "object") {
            let date = new Date(value);
            if (isNaN(date.getTime())) {
                let errorMessage = `Invalid input: FF value cannot be parsed as a date object. value: ${ value }`;
                logger.error(logger.Source.ERROR, errorMessage);
                throw new TypeError(errorMessage);
            }
            return date as T;
        }

        return value;
    }

    findMissingFeatureFlagOnBackend(): FeatureFlagNameKey | undefined {
        let allFeatureFlags = this.getAllFeatureFlags();
        for (let key in FeatureFlagName) {
            let cc: FeatureFlagNameKey = key as FeatureFlagNameKey;
            if (!allFeatureFlags?.some(l => l.name === FeatureFlagName[cc])) {
                return cc;
            }
        }
        return undefined;
    }

    observeFlagChanges(flag: FeatureFlagName, onChange: (flagStatus: IFeatureFlagStatus) => void): Unsubscribe {
        return observeStore(this.store, state => state.data.featureFlags[flag], flagStatus => {
            // flagStatus is undefined/null when the flag no longer exists in the store — e.g. the backend
            // removed it and the next downloadFeatureFlags rebuilt the store without it. Skip the
            // callback in this case; callers expect a valid IFeatureFlagStatus.
            if (flagStatus) {
                onChange(flagStatus);
            }
        });
    }
}
